import React from "react";
import { Box, Container, CircularProgress, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import MessageData from "../../../../data/MessageData";
import ReportData from "../../../../data/ReportData";
import organizationIdSelector from "../../../../atoms/organizationIdSelector";
import useEvents from "../../../../hooks/useEvents";
import useReportSummary from "../../../../hooks/useReportSummary";
import useReportCompliance from "../../../../hooks/useReportCompliance";
import { Reports } from "../../../../hooks/useReportHistory";
import ReportNavigation, { BackToTop } from "./components/ReportNavigation";
import { ReportComparisonProvider } from "../../../../contexts/ReportComparisonContext";

// Import our new component modules
import ReportHeader from "./components/ReportHeader";
import AIInsightsCard from "./components/AIInsightsCard";
import ComplianceCard from "./components/ComplianceCard";
import NutritionCard from "./components/NutritionCard";
import ExerciseCard from "./components/ExerciseCard";
import ActivitiesCard from "./components/ActivitiesCard";
import CardioGoalsCard from "./components/CardioGoalsCard";
import SupplementsCard from "./components/SupplementsCard";
import WeeklyQuestionsCard from "./components/WeeklyQuestionsCard";
import EnhancedPhotosTab from "../clients/components/EnhancedPhotosTab";
import ReportsFloatingActions from "./components/ReportsFloatingActions";
import ExtraDataQuestionsCard from "./components/ExtraDataQuestionsCard";
import useEntitlements from "../../../../hooks/useEntitlements";
import UpgradePrompt from "../../../../components/organisms/UpgradePrompt";

const ReportSummaryDrawer = () => {
  const { organizationId: routeOrganizationId, reportId } = useParams();
  const [group, setGroup] = React.useState();
  const [aiSummary, setAiSummary] = React.useState(null);
  const [loadingAI, setLoadingAI] = React.useState(false);
  const [aiError, setAiError] = React.useState(null);
  const [report, setReport] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const events = useEvents();
  const navigate = useNavigate();
  const organizationId = useRecoilValue(organizationIdSelector);
  const reports = useRecoilValue(Reports);
  const setReports = useSetRecoilState(Reports);
  const orgIdForReports = routeOrganizationId || organizationId;
  const { getSummaryFromReport } = useReportSummary();
  const compliance = useReportCompliance(report);
  const { hasFeature, organizationData } = useEntitlements();
  const canUseAiInsights = hasFeature("ai_insights");

  // Find the report from the reports list or fetch it
  React.useEffect(() => {
    if (reportId && reports.length > 0) {
      const foundReport = reports.find((r) => r.id === reportId);
      if (foundReport) {
        setReport(foundReport);
        setLoading(false);
      }
    }
  }, [reportId, reports]);

  // Hydrate AI insights from the report document (saved on first generation).
  React.useEffect(() => {
    if (!report) return;
    const c = report.aiInsightsCache;
    if (
      c &&
      typeof c.title === "string" &&
      c.title.trim() !== "" &&
      typeof c.summary === "string" &&
      c.summary.trim() !== ""
    ) {
      setAiSummary({ title: c.title, summary: c.summary });
      setAiError(null);
    }
  }, [report]);

  // Effect for message group
  React.useEffect(() => {
    async function getData() {
      if (!report) {
        return;
      }
      const group = await MessageData.getMessageGroup(report.clientData.uid);
      setGroup(group[0]);
    }
    getData();
  }, [report]);

  // Handle mark processed
  const handleMarkProcessed = (report) => {
    if (report.status === "unprocessed") {
      ReportData.processReport({ reportId: report.id });
      events.logButtonClick({
        buttonName: "Process Report",
        pageName: "Reports",
        tabName: report.id,
      });
    } else {
      ReportData.processReport({
        reportId: report.id,
        status: "unprocessed",
      });
      events.logButtonClick({
        buttonName: "Unprocess Protocol",
        pageName: "Protocols",
        tabName: report.id,
      });
    }
    // Navigate back to reports list
    navigate("/home/reports");
  };

  const loadAiInsights = React.useCallback(
    async ({ forceRefresh = false } = {}) => {
      if (!canUseAiInsights) return;
      if (!report) return;
      if (loadingAI && !forceRefresh) return;
      if (
        !forceRefresh &&
        aiSummary &&
        typeof aiSummary.title === "string" &&
        aiSummary.title.trim() !== "" &&
        typeof aiSummary.summary === "string" &&
        aiSummary.summary.trim() !== ""
      ) {
        return;
      }

      setLoadingAI(true);
      setAiError(null);
      if (forceRefresh) {
        setAiSummary(null);
      }

      const abortController = new AbortController();

      try {
        const response = await getSummaryFromReport(
          report,
          abortController.signal,
          { forceRefresh },
        );

        if (response) {
          const { title, summary, source } = response;
          setAiSummary({ title, summary });
          const shouldPersistToReport =
            orgIdForReports &&
            report.id &&
            title &&
            summary &&
            (source === "remote" ||
              (source === "localStorage" && !report.aiInsightsCache?.title));
          if (shouldPersistToReport) {
            await ReportData.saveReportAiInsightsCache(
              orgIdForReports,
              report.id,
              { title, summary },
            );
            setReports((prev) =>
              prev.map((r) =>
                r.id === report.id
                  ? {
                      ...r,
                      aiInsightsCache: {
                        title,
                        summary,
                        generatedAt: new Date(),
                      },
                    }
                  : r,
              ),
            );
          }
        } else {
          setAiError("Unable to generate AI insights at this time.");
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          setAiSummary(null);
          if (error.message?.includes?.("Rate limited")) {
            setAiError(
              "AI insights are temporarily unavailable due to high demand. Please try again in a few minutes.",
            );
          } else {
            setAiError(
              "Unable to generate AI insights at this time. Please try again later.",
            );
          }
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoadingAI(false);
        }
      }
    },
    [
      canUseAiInsights,
      report,
      getSummaryFromReport,
      loadingAI,
      aiSummary,
      orgIdForReports,
      setReports,
    ],
  );

  const handleAiAccordionExpand = React.useCallback(() => {
    loadAiInsights({ forceRefresh: false });
  }, [loadAiInsights]);

  const retryAISummary = React.useCallback(() => {
    loadAiInsights({ forceRefresh: true });
  }, [loadAiInsights]);

  if (loading || !report) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: 2,
            minHeight: "60vh",
          }}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading report...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <ReportComparisonProvider
      currentReport={report}
      organizationId={organizationId}
    >
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Report header with client info and action buttons */}
        <ReportHeader
          report={report}
          onClientClick={() => {
            const clientUid = report.clientData.uid;
            navigate(`/home/clients/summary/${clientUid}/${organizationId}`);
            events.logButtonClick({
              buttonName: "View Client Profile",
              pageName: "Reports",
              tabName: clientUid,
            });
          }}
          handleMarkProcessed={handleMarkProcessed}
        />

        {/* Navigation bar */}
        <ReportNavigation report={report} />

        {/* Main content area with cards in a vertical layout */}
        <Box sx={{ position: "relative" }}>
          {/* AI Insights card */}
          <Box id="ai-insights" sx={{ position: "relative" }}>
            {canUseAiInsights ? (
              <AIInsightsCard
                response={aiSummary}
                loading={loadingAI}
                error={aiError}
                onRetry={retryAISummary}
                onExpandSection={handleAiAccordionExpand}
              />
            ) : (
              <UpgradePrompt
                organizationData={organizationData}
                minTier="pro"
                title="Upgrade for report AI insights"
                description="AI report summaries are available on Pro."
                featureName="ai_insights"
              />
            )}
          </Box>

          {/* Compliance score card */}
          <Box id="compliance" sx={{ position: "relative" }}>
            <ComplianceCard
              compliance={compliance}
              uid={report?.clientData?.uid}
              organizationId={organizationId}
            />
          </Box>

          {/* Extra data questions card */}
          <Box id="extra-data" sx={{ position: "relative" }}>
            <ExtraDataQuestionsCard report={report} />
          </Box>

          {/* Nutrition summary card */}
          <Box id="nutrition" sx={{ position: "relative" }}>
            <NutritionCard report={report} />
            <BackToTop />
          </Box>

          {/* Exercise data card */}
          <Box id="exercise" sx={{ position: "relative" }}>
            <ExerciseCard report={report} />
            <BackToTop />
          </Box>

          {/* Health activities from Apple Health */}
          <Box id="activities" sx={{ position: "relative" }}>
            <ActivitiesCard report={report} />
            <BackToTop />
          </Box>

          {/* Cardio goals card */}
          <Box id="cardio" sx={{ position: "relative" }}>
            <CardioGoalsCard report={report} />
            <BackToTop />
          </Box>

          {/* Supplements card */}
          <Box id="supplements" sx={{ position: "relative" }}>
            <SupplementsCard report={report} />
            <BackToTop />
          </Box>

          {/* Weekly questions card */}
          <Box id="questions" sx={{ position: "relative" }}>
            <WeeklyQuestionsCard report={report} />
            <BackToTop />
          </Box>

          {/* Progress photos card - using the same enhanced component as client summary */}
          <Box id="photos" sx={{ position: "relative" }}>
            <EnhancedPhotosTab
              embedded
              client={{
                uid: report?.clientData?.uid,
                firstName: report?.clientFirstName,
                lastName: report?.clientLastName,
                ...report?.clientData,
              }}
              photoUpdate={null}
            />
            <BackToTop />
          </Box>

          {/* Messages card */}
          {/* <MessagesCard report={report} group={group} /> */}
        </Box>

        {/* Add the floating actions component for messages */}
        <ReportsFloatingActions
          messageGroup={group}
          client={report.clientData}
        />
      </Container>
    </ReportComparisonProvider>
  );
};

export default ReportSummaryDrawer;
